// @ts-nocheck
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LazyImage from "@insite/mobius/LazyImage";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useProductContext from "@store/Framework/Store/Data/Products/ProductsHooks/useProductContext";
import ProductPrimaryImage, { ProductPrimaryImageStyles } from "@store/Overrides/Components/ProductPrimaryImage";
import { primaryImageStyles } from "@store/Overrides/Widgets/ProductDetails/ProductDetailsPrimaryImage";

import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { ThemeTypography } from "@insite/mobius/globals/baseTheme";
import Link from "@store/MobiusOverrides/Link/Link";
import {
    carouselFieldDefinitions,
    CarouselFieldProps,
    getExtendedCarouselStyles,
} from "@store/Framework/Common/Utilities/CarouselWidgetHelpers";
import typographyFieldDictionary from "@store/Framework/Features/FieldDefinitionBuilder/Fields/typographyFieldDictionary";
import { FoundationFieldDefinition } from "@store/Framework/Types/FoundationFieldDefinition";
import Typography from "@store/MobiusOverrides/Typography/Typography";
import Carousel, { CarouselSlide } from "@store/Overrides/Components/Carousel";
import React from "react";
import { useMemo } from "react";
import { css } from "styled-components";

enum fields {
    customColor = "customColor",
    titleStyle = "titleStyle",
    ModalLinkText = "ModalLinkText",
}

interface SizeChartModalProps extends WidgetProps {
    fields: {
        [fields.customColor]: string;
        [fields.titleStyle]: keyof ThemeTypography | undefined;
        [fields.ModalLinkText]: string;
    } & CarouselFieldProps;
}

interface SizeChartStyles {
    carouselImage?: InjectableCss;
    modalStyles?: ModalPresentationProps;
    primaryImage?: ProductPrimaryImageStyles;
}

const sizeChartStyles: SizeChartStyles = {
    carouselImage: {
        css: css`
            > img {
                max-width: 100%;
                padding-block: 1rem;
                max-height: 70vh;
                object-fit: contain;
            }
        `,
    },
    modalStyles: primaryImageStyles.fullPrimaryImageModal,
    primaryImage: primaryImageStyles.fullPrimaryImage,
};

const SizeChartModal: React.FC<SizeChartModalProps> = ({ fields, fields: { titleStyle, ModalLinkText } }) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const { isInShell } = useShellContext();
    const styles = useMemo(() => sizeChartStyles, []);
    const product = useProductContext()?.product;
    const extendedCarouselStyles = getExtendedCarouselStyles(fields);
    const showDots = fields.showDots;

    const images = useMemo(
        () => product.images?.filter(image => image.name.toLocaleLowerCase().includes("sizechart")) ?? [],
        [product.images],
    );
    const slides: CarouselSlide[] = useMemo(
        () =>
            images.map(image => ({
                id: image.id,
                renderComponent: () => {
                    return <LazyImage key={image.id} {...styles.carouselImage} src={image.largeImagePath} />;
                },
            })),
        [images],
    );
    if (!images.length && !isInShell) {
        return null;
    }
    const TypographyStyles = {
        css: css`
            color: ${fields.customColor};
        `,
    };
    const LinkStyles = {
        css: css`
            &:hover {
                color: ${fields.customColor};
            }
        `,
    };
    return (
        <>
            <Link {...LinkStyles} mergeCss onClick={() => setIsOpen(true)}>
                <Typography {...TypographyStyles} variant={titleStyle}>
                    {translate(ModalLinkText)}
                </Typography>
            </Link>

            <Modal {...styles.modalStyles} isOpen={isOpen} handleClose={() => setIsOpen(false)}>
                {/* bunzl custom code start */}
                {images.length <= 1 && (
                    <ProductPrimaryImage
                        product={product}
                        image={images[0]}
                        useLargeImage={true}
                        extendedStyles={styles.primaryImage}
                        showLoader={true}
                    />
                )}
                {images.length > 1 && (
                    <Carousel
                        id="productDetailsImageCarousel"
                        slides={slides}
                        extendedStyles={extendedCarouselStyles}
                        showDots={showDots}
                        maxNumberOfColumns={1}
                    />
                )}
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule<FoundationFieldDefinition> = {
    component: SizeChartModal,
    definition: {
        group: "Product Details",
        displayName: "Size Chart Modal",
        fieldDefinitions: [
            ...carouselFieldDefinitions,
            {
                name: fields.ModalLinkText,
                displayName: "Link Text",
                editorTemplate: "TextField",
                defaultValue: "Size Chart",
                isRequired: true,
                fieldType: "General",
            },
            {
                name: fields.customColor,
                displayName: "Custom Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "rgba(0,0,0,100)",
                fieldType: "General",
            },
            typographyFieldDictionary.titleStyle,
        ],
    },
};

export default widgetModule;
